import React, { createContext, useContext, useState, useEffect } from 'react';

// Define the shape of a Plan object based on usage in HostingPlans
// Ideally this should be imported from a shared types file if it exists
export interface Plan {
    id: string;
    name: string;
    displayName: string;
    price: number;
    currency: string;
    payType: string;
    payTypeDisplay?: string;
    resources: string;
    resourcesDetail?: string | null;
    dataCenterSelectable: boolean;
    dataCenterDetail?: string | null;
    sites: string;
    hostingSystem: string;
    ssl: boolean;
    spaceGb: number;
    bandwidth: string | null;
    emails: number;
    backups: boolean;
    backupsDetail?: string | null;
    security: string;
    securityDetail?: string | null;
    cache: string;
    imageOptimization: string;
    mainPhotoUrl?: string | null;
    [key: string]: any;
}

interface ComparisonContextType {
    selectedPlans: Plan[];
    togglePlan: (plan: Plan) => void;
    setPlans: (plans: Plan[]) => void;
    clearSelection: () => void;
    isInComparison: (planId: string) => boolean;
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);

export const ComparisonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedPlans, setSelectedPlans] = useState<Plan[]>([]);

    // Load from session storage on mount (optional, to persist across refresh if desired)
    // For now we stick to simple state, or maybe sessionStorage to be safe.
    useEffect(() => {
        const saved = sessionStorage.getItem('comparison_plans');
        if (saved) {
            try {
                setSelectedPlans(JSON.parse(saved));
            } catch (e) {
                console.error('Failed to parse saved plans', e);
            }
        }
    }, []);

    useEffect(() => {
        sessionStorage.setItem('comparison_plans', JSON.stringify(selectedPlans));
    }, [selectedPlans]);

    const togglePlan = (plan: Plan) => {
        setSelectedPlans((prev) => {
            const exists = prev.find((p) => p.id === plan.id);
            if (exists) {
                // Remove
                return prev.filter((p) => p.id !== plan.id);
            } else {
                // Add (max 4 as per UI hint in screenshot "comparar hasta 4 hostings")
                if (prev.length >= 4) {
                    // Optional: Alert user or just don't add
                    // For now we just return prev
                    return prev;
                }
                return [...prev, plan];
            }
        });
    };

    const clearSelection = () => {
        setSelectedPlans([]);
    };

    const setPlans = (plans: Plan[]) => {
        setSelectedPlans(plans);
    };

    const isInComparison = (planId: string) => {
        return selectedPlans.some((p) => p.id === planId);
    };

    return (
        <ComparisonContext.Provider value={{ selectedPlans, togglePlan, setPlans, clearSelection, isInComparison }}>
            {children}
        </ComparisonContext.Provider>
    );
};

export const useComparison = () => {
    const context = useContext(ComparisonContext);
    if (context === undefined) {
        throw new Error('useComparison must be used within a ComparisonProvider');
    }
    return context;
};
